import React, { useState, useEffect } from 'react';
import { PAIEMENT_ACTIF } from '../config/features';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '../i18n/router';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  Avatar,
} from '@mui/material';
import { useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import AssuranceVisaComponent from '../components/AssuranceVisaComponent';
import { API_BASE_URL } from '../config/api';
import { recupererListeVoyageurs, checkUserSociete } from '../Services';
import { Voyageurs } from '../models/Voyageurs';

interface FormScreenRouteParams {
  title: string;
  prixconsulat: number;
  prixintervention: number;
  demandeId: number;
  url_flag: string;
  validite: string;
  informations: string;
  commentaires?: string;
  titleDisplay?: string;
}

const FormUSA: React.FC = () => {
  const { t } = useTranslation('formulaires');
  const { t: tp } = useTranslation('formulaires_pays');
  // Récupération des paramètres passés en state via react-router
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromVoyageurCreation = searchParams.get('fromCreation') === 'true';

  // Essayer de récupérer le state depuis location.state ou sessionStorage
  const getInitialState = (): FormScreenRouteParams => {
    // Si on revient de la création de voyageur, utiliser sessionStorage
    if (fromVoyageurCreation) {
      const saved = sessionStorage.getItem('formState');
      if (saved) {
        return JSON.parse(saved) as FormScreenRouteParams;
      }
    }
    // Sinon utiliser location.state et nettoyer sessionStorage
    if (location.state && Object.keys(location.state).length > 0) {
      sessionStorage.removeItem('formState');
      return location.state as FormScreenRouteParams;
    }
    return {} as FormScreenRouteParams;
  };
  const state = getInitialState();
  const titleDisplay = (state as any)?.titleDisplay || (state as any)?.title || '';
  const demandeId = state.demandeId;
  const isEditMode = demandeId != null;

  // Champs issus des paramètres de route
  const [title, setTitle] = useState<string>(state?.title || '');
  const [prixconsulat, setPrixconsulat] = useState<number>(state?.prixconsulat || 0);
  const [prixintervention, setPrixintervention] = useState<number>(state?.prixintervention || 0);
  const [urlFlag, setUrlFlag] = useState<string>(state?.url_flag || '');
  const [validite, setValidite] = useState<string>(state?.validite || '');
  const commentaires = state?.commentaires || '';
  const [informations, setInformations] = useState<string>(state?.informations || '');

  // Champs communs
  const [dateDepart, setDateDepart] = useState<string>('');
  const [dateSortie, setDateSortie] = useState<string>('');
  const [visiteUSA, setVisiteUSA] = useState<string>(''); // "oui" ou "non"
  const [numVisa, setNumVisa] = useState<string>('');
  const [selectedVoyageur, setSelectedVoyageur] = useState<string>('');
  const [voyageurs, setVoyageurs] = useState<Voyageurs[]>([]);
  const [assuranceVisa, setAssuranceVisa] = useState<string>('');
  const [sliderValue, setSliderValue] = useState<boolean>(false);

  // Nouveaux champs
  const [nomPrenomPere, setNomPrenomPere] = useState<string>('');
  const [nomPrenomMere, setNomPrenomMere] = useState<string>('');
  const [nomPrenomContactUrgence, setNomPrenomContactUrgence] = useState<string>('');
  const [telephoneContactUrgence, setTelephoneContactUrgence] = useState<string>('');
  const [mailContactUrgence, setMailContactUrgence] = useState<string>('');
  const [adresseHebergement, setAdresseHebergement] = useState<string>('');
  const [codePostalHebergement, setCodePostalHebergement] = useState<string>('');
  const [villeHebergement, setVilleHebergement] = useState<string>('');
  const [telephoneHebergement, setTelephoneHebergement] = useState<string>('');
  const [mailHebergement, setMailHebergement] = useState<string>('');
  const [nomEmployeur, setNomEmployeur] = useState<string>('');
  const [adresseEmployeur, setAdresseEmployeur] = useState<string>('');
  const [codePostalEmployeur, setCodePostalEmployeur] = useState<string>('');
  const [villeEmployeur, setVilleEmployeur] = useState<string>('');
  const [telephoneEmployeur, setTelephoneEmployeur] = useState<string>('');
  const [mailEmployeur, setMailEmployeur] = useState<string>('');
  const [visitePaysInterdits, setVisitePaysInterdits] = useState<string>(''); // "oui" ou "non"
  const [champsNonRemplis, setChampsNonRemplis] = useState<string[]>([]);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hideFees] = useState<boolean>(false);
  const [isClientSociete, setIsClientSociete] = useState<boolean | null>(null);
  const [showSuccessScreen, setShowSuccessScreen] = useState<boolean>(false);
  const [createdDemandeId, setCreatedDemandeId] = useState<number | null>(null);

  // Calcul du total
  const total =
    prixconsulat + prixintervention + (assuranceVisa.toLowerCase() === 'oui' ? 20 : 0);

  // Fonction de validation du formulaire
  const validateForm = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const parseDate = (dateStr: string): Date | null => {
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
      }
      return null;
    };

    const departDate = parseDate(dateDepart);
    const sortieDate = parseDate(dateSortie);

    const errors: string[] = [];

    // Contrôle des dates
    if (departDate) {
      if (isNaN(departDate.getTime()) || departDate <= today) {
        errors.push(tp('usa.errors.departureDateInvalid'));
      }
    } else {
      errors.push(tp('usa.errors.departureDateRequired'));
    }

    if (dateSortie) {
      if (sortieDate && departDate && (isNaN(sortieDate.getTime()) || sortieDate <= departDate)) {
        errors.push(tp('usa.errors.returnDateInvalid'));
      } else if (!sortieDate) {
        errors.push(tp('usa.errors.returnDateRequired'));
      }
    }

    // Validation des champs obligatoires
    if (!selectedVoyageur) errors.push(t('common.validation.travelerNotSelected'));
    if (!dateDepart.trim()) errors.push(t('common.validation.departureDate'));
    if (!dateSortie.trim()) errors.push(t('common.validation.returnDate'));
    if (!visiteUSA.trim()) errors.push(tp('usa.validation.visiteUSA'));
    if (visiteUSA.toLowerCase() === 'oui' && !numVisa.trim()) {
      errors.push(tp('usa.validation.numVisa'));
    }
    if (!assuranceVisa.trim()) errors.push(t('common.validation.insurance'));
    if (!sliderValue) errors.push(t('common.validation.cguAcceptance'));

    // Champs supplémentaires
    if (!nomPrenomPere.trim()) errors.push(tp('usa.validation.nomPrenomPere'));
    if (!nomPrenomMere.trim()) errors.push(tp('usa.validation.nomPrenomMere'));
    if (!nomPrenomContactUrgence.trim()) errors.push(tp('usa.validation.nomPrenomContactUrgence'));
    if (!telephoneContactUrgence.trim()) errors.push(tp('usa.validation.telephoneContactUrgence'));
    if (!mailContactUrgence.trim()) errors.push(tp('usa.validation.mailContactUrgence'));
    if (!adresseHebergement.trim()) errors.push(tp('usa.validation.adresseHebergement'));
    if (!codePostalHebergement.trim()) errors.push(tp('usa.validation.codePostalHebergement'));
    if (!villeHebergement.trim()) errors.push(tp('usa.validation.villeHebergement'));
    if (!telephoneHebergement.trim()) errors.push(tp('usa.validation.telephoneHebergement'));
    if (!mailHebergement.trim()) errors.push(tp('usa.validation.mailHebergement'));
    if (!nomEmployeur.trim()) errors.push(tp('usa.validation.nomEmployeur'));
    if (!adresseEmployeur.trim()) errors.push(tp('usa.validation.adresseEmployeur'));
    if (!codePostalEmployeur.trim()) errors.push(tp('usa.validation.codePostalEmployeur'));
    if (!villeEmployeur.trim()) errors.push(tp('usa.validation.villeEmployeur'));
    if (!telephoneEmployeur.trim()) errors.push(tp('usa.validation.telephoneEmployeur'));
    if (!mailEmployeur.trim()) errors.push(tp('usa.validation.mailEmployeur'));
    if (!visitePaysInterdits.trim()) errors.push(tp('usa.validation.visitePaysInterdits'));
    if (visitePaysInterdits.toLowerCase() === 'oui') {
      errors.push(tp('usa.warnings.estaRestrictedCountries'));
    }

    setChampsNonRemplis(errors);
    setIsFormValid(errors.length === 0);
  };

  useEffect(() => {
    validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dateDepart,
    dateSortie,
    visiteUSA,
    numVisa,
    assuranceVisa,
    sliderValue,
    selectedVoyageur,
    nomPrenomPere,
    nomPrenomMere,
    nomPrenomContactUrgence,
    telephoneContactUrgence,
    mailContactUrgence,
    adresseHebergement,
    codePostalHebergement,
    villeHebergement,
    telephoneHebergement,
    mailHebergement,
    nomEmployeur,
    adresseEmployeur,
    codePostalEmployeur,
    villeEmployeur,
    telephoneEmployeur,
    mailEmployeur,
    visitePaysInterdits,
  ]);

  // Récupération de la liste des voyageurs depuis le localStorage
  useEffect(() => {
    const fetchVoyageurs = async () => {
      const userJson = localStorage.getItem('user');
      if (userJson) {
        const user = JSON.parse(userJson);
        const listeVoyageurs = await recupererListeVoyageurs(user.id_utilisateur);
        setVoyageurs(listeVoyageurs);
        if (listeVoyageurs.length === 1) {
          setSelectedVoyageur(listeVoyageurs[0].id.toString());
        } else {
          setSelectedVoyageur('');
        }
      }
    };
    fetchVoyageurs();
  }, [demandeId]);

  // Vérification si l'utilisateur appartient à une société
  useEffect(() => {
    const checkUserStatus = async () => {
      try {
        const userJson = localStorage.getItem('user');
        if (userJson) {
          const user = JSON.parse(userJson);
          const societeResult = await checkUserSociete(user.id_utilisateur);
          setIsClientSociete(societeResult.isSociete);
        }
      } catch (error) {
        console.error('Erreur lors de la vérification du statut utilisateur:', error);
        setIsClientSociete(true);
      }
    };
    checkUserStatus();
  }, []);

  const selectedVoyageurObj = voyageurs.find(
    (v) => v.id.toString() === selectedVoyageur
  );

  // Empêcher la fermeture accidentelle de l’onglet
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const handleBack = () => {
    if (
      window.confirm(t('common.confirmation.unsavedChanges'))
    ) {
      navigate(-1);
    }
  };

  // Formatage de la date (jj/mm/aaaa)
  const formatDate = (input: string) => {
    const numbers = input.replace(/[^\d]/g, '');
    let formatted = '';
    for (let i = 0; i < numbers.length && i < 8; i++) {
      if (i === 2 || i === 4) formatted += '/';
      formatted += numbers[i];
    }
    return formatted;
  };

  // Gestion de la soumission du formulaire
  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const userJson = localStorage.getItem('user');
      let userId: string | null = null;
      if (userJson) {
        const user = JSON.parse(userJson);
        userId = user.id_utilisateur;
      }

      const etatFormulaire = champsNonRemplis.length === 0;
      const data = {
        etatFormulaire,
        idUtilisateur: userId || '',
        idVoyageur: selectedVoyageur,
        titreVisa: title,
        dateDepart,
        dateSortie,
        visiteUSA,
        numVisa: visiteUSA.toLowerCase() === 'oui' ? numVisa : '',
        nomPrenomPere,
        nomPrenomMere,
        nomPrenomContactUrgence,
        telephoneContactUrgence,
        mailContactUrgence,
        adresseHebergement,
        codePostalHebergement,
        villeHebergement,
        telephoneHebergement,
        mailHebergement,
        nomEmployeur,
        adresseEmployeur,
        codePostalEmployeur,
        villeEmployeur,
        telephoneEmployeur,
        mailEmployeur,
        visitePaysInterdits,
        assuranceVisa,
        prixconsulat,
        prixintervention,
        validite,
      };

      // Envoi des données au backend
      const response = await axios.post(
        `${API_BASE_URL}/validerDemande`,
        data
      );
      setIsLoading(false);

      // Stocker l'ID de la demande créée si disponible
      if (response && response.data && response.data.idDemande) {
        setCreatedDemandeId(response.data.idDemande);
      }

      // Afficher l'écran de succès au lieu de naviguer directement
      setShowSuccessScreen(true);
    } catch (error) {
      console.error('Erreur lors de la validation de la demande:', error);
      setIsLoading(false);
    }
  };

  // Navigation vers le paiement SumUp (pour particuliers hors société)
  const handlePayerEnLigne = () => {
    if (createdDemandeId) {
      navigate('/paiement', {
        state: {
          bonId: 0,
          urlPaiement: '',
          demandeIds: [createdDemandeId],
          modePaiementSumup: true,
        },
      });
    } else {
      navigate('/retour', { state: { tabValue: 2, showPaymentMessage: true } });
    }
  };

  // Créer une nouvelle demande (reste sur le même formulaire)
  const handleNouvelleDemande = () => {
    setShowSuccessScreen(false);
    setCreatedDemandeId(null);
    setDateDepart('');
    setDateSortie('');
    setSelectedVoyageur(voyageurs.length === 1 ? voyageurs[0].id.toString() : '');
    setAssuranceVisa('');
    setSliderValue(false);
    setVisiteUSA('');
    setNumVisa('');
    setNomPrenomPere('');
    setNomPrenomMere('');
    setNomPrenomContactUrgence('');
    setTelephoneContactUrgence('');
    setMailContactUrgence('');
    setAdresseHebergement('');
    setCodePostalHebergement('');
    setVilleHebergement('');
    setTelephoneHebergement('');
    setMailHebergement('');
    setNomEmployeur('');
    setAdresseEmployeur('');
    setCodePostalEmployeur('');
    setVilleEmployeur('');
    setTelephoneEmployeur('');
    setMailEmployeur('');
    setVisitePaysInterdits('');
  };

  // Fonction vide pour onClick si disabled
  const disabledPressHandler = () => {};

  // Si l'écran de succès est affiché
  if (showSuccessScreen) {
    return (
      <Box
        sx={{ backgroundColor: '#ECE4D3', minHeight: '100vh', py: 4 }}
      >
        <Container maxWidth="md">
          <Box sx={{ backgroundColor: '#fff', p: 4, borderRadius: 2, boxShadow: 2, textAlign: 'center' }}>
            {/* En-tête avec drapeau */}
            {urlFlag && (
              <Box
                component="img"
                src={urlFlag}
                alt={t('common.alt.flag')}
                sx={{ width: 60, height: 36, mb: 2, mx: 'auto' }}
              />
            )}

            {/* Message de succès */}
            <Typography variant="h4" sx={{ color: 'green', fontWeight: 'bold', mb: 2 }}>
              {t('success.title')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t('success.message', { title: titleDisplay })}
            </Typography>

            {/* Boutons conditionnels selon le type d'utilisateur */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', mt: 4 }}>
              {/* Bouton Payer en ligne - uniquement pour particuliers (hors societe) */}
              {isClientSociete === false && !hideFees && (
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handlePayerEnLigne}
                  sx={{ minWidth: 280 }}
                >
                  {PAIEMENT_ACTIF ? t('success.payOnline') : t('success.launchDemande')}
                </Button>
              )}

              {/* Bouton Creer une nouvelle demande - pour tous les utilisateurs */}
              <Button
                variant="outlined"
                color="primary"
                size="large"
                onClick={handleNouvelleDemande}
                sx={{ minWidth: 280 }}
              >
                {t('success.newRequest')}
              </Button>

              {/* Bouton Voir mes demandes */}
              <Button
                variant="text"
                color="inherit"
                onClick={() => navigate('/retour', { state: { tabValue: 2 } })}
                sx={{ mt: 1 }}
              >
                {t('success.viewRequests')}
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      component="form"
      noValidate
      autoComplete="off"
      sx={{ backgroundColor: '#ECE4D3', minHeight: '100vh', py: 4 }}
    >
      <Container maxWidth="md">
        <Box sx={{ backgroundColor: '#fff', p: 3, borderRadius: 2, boxShadow: 2 }}>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            {urlFlag && (
              <Box
                component="img"
                src={urlFlag}
                alt={t('common.alt.flag')}
                sx={{ width: 50, height: 30, mb: 1, mx: 'auto' }}
              />
            )}
            <Typography variant="h5" component="h1" gutterBottom>
              {t('common.formTitle', { title: titleDisplay })}
            </Typography>
            <Typography variant="body1" gutterBottom>
              {validite}
              <br />
              {informations}
            </Typography>
            <Button variant="outlined" onClick={handleBack} sx={{ mt: 1 }}>
              {t('common.back')}
            </Button>
          </Box>

          {/* ---- Question sur la visite de pays interdits (placée en premier) ---- */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="visitePaysInterdits-label">
              {tp('usa.questions.visitePaysInterdits')}
            </InputLabel>
            <Select
              labelId="visitePaysInterdits-label"
              id="visitePaysInterdits-select"
              value={visitePaysInterdits}
              label={tp('usa.questions.visitePaysInterditsShort')}
              onChange={(e: SelectChangeEvent) => setVisitePaysInterdits(e.target.value)}
            >
              <MenuItem value="oui">{t('common.yes')}</MenuItem>
              <MenuItem value="non">{t('common.no')}</MenuItem>
            </Select>
          </FormControl>

          {visitePaysInterdits.toLowerCase() === 'oui' && (
            <Box sx={{ backgroundColor: '#ffcccb', p: 2, mb: 2 }}>
              <Typography variant="body1" align="center">
                {tp('usa.warnings.estaRestrictedCountries')}
              </Typography>
            </Box>
          )}
          {/* ---- Fin question pays interdits ---- */}

          {/* Affichage des commentaires du visa en HTML */}
          {commentaires && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                backgroundColor: '#fff3cd',
                borderRadius: 1,
                border: '1px solid #ffc107',
              }}
              dangerouslySetInnerHTML={{ __html: commentaires }}
            />
          )}

          {/* Sélection du voyageur si non en mode édition */}
          {!isEditMode && (
            <>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="voyageur-select-label">{t('common.selectTraveler')}</InputLabel>
                <Select
                  labelId="voyageur-select-label"
                  id="voyageur-select"
                  value={selectedVoyageur}
                  label={t('common.selectTraveler')}
                  onChange={(e: SelectChangeEvent) => setSelectedVoyageur(e.target.value)}
                >
                  {voyageurs.map((v) => (
                    <MenuItem key={v.id} value={v.id.toString()}>
                      {v.nom} {v.prenom}
                      {v.numeropax ? t('common.selectTravelerWithPassport', { passport: v.numeropax }) : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Bouton pour créer un nouveau voyageur */}
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    sessionStorage.setItem('formState', JSON.stringify({
                      title,
                      prixconsulat,
                      prixintervention,
                      url_flag: urlFlag,
                      validite,
                      informations,
                      commentaires
                    }));
                    navigate(`/formulaire_creation_voyageur?returnUrl=${location.pathname}`);
                  }}
                  sx={{
                    color: '#D89D2F',
                    borderColor: '#D89D2F',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      borderColor: '#B5811F'
                    }
                  }}
                >
                  {t('common.createNewTraveler')}
                </Button>
              </Box>
            </>
          )}

          {/* Avatar du voyageur sélectionné */}
          {selectedVoyageurObj && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Avatar
                src={selectedVoyageurObj.url || 'https://example.com/default-avatar.png'}
                alt={t('common.alt.avatar')}
                sx={{ width: 100, height: 100 }}
              />
            </Box>
          )}

          {/* Champs pour les dates */}
          <TextField
            label={t('common.departureDate.shortLabel')}
            placeholder={t('common.departureDate.placeholder')}
            value={dateDepart}
            onChange={(e) => setDateDepart(formatDate(e.target.value))}
            fullWidth
            margin="normal"
          />
          <TextField
            label={t('common.returnDate.shortLabel')}
            placeholder={t('common.returnDate.placeholder')}
            value={dateSortie}
            onChange={(e) => setDateSortie(formatDate(e.target.value))}
            fullWidth
            margin="normal"
          />

          {/* Visite USA */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="visiteUSA-label">{tp('usa.questions.visiteUSA')}</InputLabel>
            <Select
              labelId="visiteUSA-label"
              id="visiteUSA-select"
              value={visiteUSA}
              label={tp('usa.questions.visiteUSA')}
              onChange={(e: SelectChangeEvent) => setVisiteUSA(e.target.value)}
            >
              <MenuItem value="oui">{t('common.yes')}</MenuItem>
              <MenuItem value="non">{t('common.no')}</MenuItem>
            </Select>
          </FormControl>
          {visiteUSA.toLowerCase() === 'oui' && (
            <TextField
              label={tp('usa.fields.numVisa.label')}
              placeholder={tp('usa.fields.numVisa.placeholder')}
              value={numVisa}
              onChange={(e) => setNumVisa(e.target.value)}
              fullWidth
              margin="normal"
            />
          )}

          {/* Champs supplémentaires */}
          <TextField
            label={tp('usa.fields.nomPrenomPere')}
            placeholder={tp('usa.fields.nomPrenomPere')}
            value={nomPrenomPere}
            onChange={(e) => setNomPrenomPere(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label={tp('usa.fields.nomPrenomMere')}
            placeholder={tp('usa.fields.nomPrenomMere')}
            value={nomPrenomMere}
            onChange={(e) => setNomPrenomMere(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label={tp('usa.fields.nomPrenomContactUrgence.label')}
            placeholder={tp('usa.fields.nomPrenomContactUrgence.placeholder')}
            value={nomPrenomContactUrgence}
            onChange={(e) => setNomPrenomContactUrgence(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label={tp('usa.fields.telephoneContactUrgence')}
            placeholder={tp('usa.fields.telephoneContactUrgence')}
            value={telephoneContactUrgence}
            onChange={(e) => setTelephoneContactUrgence(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label={tp('usa.fields.mailContactUrgence')}
            placeholder={tp('usa.fields.mailContactUrgence')}
            value={mailContactUrgence}
            onChange={(e) => setMailContactUrgence(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label={tp('usa.fields.adresseHebergement')}
            placeholder={tp('usa.fields.adresseHebergement')}
            value={adresseHebergement}
            onChange={(e) => setAdresseHebergement(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label={tp('usa.fields.codePostalHebergement.label')}
            placeholder={tp('usa.fields.codePostalHebergement.placeholder')}
            value={codePostalHebergement}
            onChange={(e) => setCodePostalHebergement(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label={tp('usa.fields.villeHebergement.label')}
            placeholder={tp('usa.fields.villeHebergement.placeholder')}
            value={villeHebergement}
            onChange={(e) => setVilleHebergement(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label={tp('usa.fields.telephoneHebergement.label')}
            placeholder={tp('usa.fields.telephoneHebergement.placeholder')}
            value={telephoneHebergement}
            onChange={(e) => setTelephoneHebergement(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label={tp('usa.fields.mailHebergement.label')}
            placeholder={tp('usa.fields.mailHebergement.placeholder')}
            value={mailHebergement}
            onChange={(e) => setMailHebergement(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label={tp('usa.fields.nomEmployeur')}
            placeholder={tp('usa.fields.nomEmployeur')}
            value={nomEmployeur}
            onChange={(e) => setNomEmployeur(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label={tp('usa.fields.adresseEmployeur')}
            placeholder={tp('usa.fields.adresseEmployeur')}
            value={adresseEmployeur}
            onChange={(e) => setAdresseEmployeur(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label={tp('usa.fields.codePostalEmployeur.label')}
            placeholder={tp('usa.fields.codePostalEmployeur.placeholder')}
            value={codePostalEmployeur}
            onChange={(e) => setCodePostalEmployeur(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label={tp('usa.fields.villeEmployeur.label')}
            placeholder={tp('usa.fields.villeEmployeur.placeholder')}
            value={villeEmployeur}
            onChange={(e) => setVilleEmployeur(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label={tp('usa.fields.telephoneEmployeur.label')}
            placeholder={tp('usa.fields.telephoneEmployeur.placeholder')}
            value={telephoneEmployeur}
            onChange={(e) => setTelephoneEmployeur(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label={tp('usa.fields.mailEmployeur.label')}
            placeholder={tp('usa.fields.mailEmployeur.placeholder')}
            value={mailEmployeur}
            onChange={(e) => setMailEmployeur(e.target.value)}
            fullWidth
            margin="normal"
          />

          {/* Module Assurance */}
          <AssuranceVisaComponent
            assuranceVisa={assuranceVisa}
            setAssuranceVisa={setAssuranceVisa}
            sliderValue={sliderValue}
            setSliderValue={setSliderValue}
          />

          {/* Récapitulatif du total */}
          {!hideFees && (
            <Box sx={{ textAlign: 'center', my: 2 }}>
              <Typography variant="body1">{t('common.fees.visa', { prix: prixconsulat })}</Typography>
              <Typography variant="body1">{t('common.fees.service', { prix: prixintervention })}</Typography>
              <Typography variant="h6" sx={{ mt: 1 }}>
                {t('common.fees.total', { total })}
              </Typography>
            </Box>
          )}

          {/* Affichage des erreurs / état du formulaire */}
          <Box
            sx={{
              my: 2,
              p: 2,
              borderRadius: 1,
              backgroundColor: champsNonRemplis.length > 0 ? '#ffcccb' : '#ccffcc',
            }}
          >
            {champsNonRemplis.length > 0 ? (
              <>
                <Typography variant="h6" align="center">
                  {t('common.formStatus.incomplete')}
                </Typography>
                {champsNonRemplis.map((champ, index) => (
                  <Typography key={index}>{champ}</Typography>
                ))}
              </>
            ) : (
              <Typography align="center">{t('common.formStatus.complete')}</Typography>
            )}
          </Box>

          {/* Bouton de soumission */}
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', my: 2 }}>
              <Button
                variant="contained"
                onClick={isFormValid ? handleSubmit : disabledPressHandler}
                disabled={!isFormValid}
                sx={{ backgroundColor: isFormValid ? 'black' : '#A9A9A9' }}
              >
                {t('common.submit')}
              </Button>
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default FormUSA;
